import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MessageSquare, TrendingUp, Users, Package, Banknote, ShieldCheck } from "lucide-react";
import SupportWidget from "@/components/SupportWidget";
import WhatsAppCta, { useWhatsAppCta } from "@/components/WhatsAppCta";
import WhatsAppChatDemo from "@/components/WhatsAppChatDemo";

const HERO_IMG = "https://images.unsplash.com/photo-1773858437808-509ceef36d67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxuaWdlcmlhbiUyMG1hcmtldCUyMHRyYWRlciUyMHdvbWFuJTIwc21pbGluZ3xlbnwwfHx8fDE3ODIwNTgxODZ8MA&ixlib=rb-4.1.0&q=85";
const TESTI_IMG = "https://images.pexels.com/photos/8475194/pexels-photo-8475194.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Pre-launch: single call to action across the whole marketing page.
const WAITLIST_URL = "https://chat.whatsapp.com/BjFkahLjCa4GTsujlvmOqO";
const WAITLIST_SUBTEXT = "Join our WhatsApp community for exclusive early access and launch updates.";

const features = [
  { icon: MessageSquare, title: "Talk to your books", desc: "Send a chat or voice note in Pidgin or English. Your books update themselves." },
  { icon: Users, title: "Who owe you?", desc: "Track every customer who took goods on credit. Send polite reminders in one tap." },
  { icon: TrendingUp, title: "Real cash clarity", desc: "Wake up to your daily money pulse: sales in, expenses out, what's coming." },
  { icon: Package, title: "Stock alerts", desc: "Never run out of fast moving items. Kobolog tells you when to restock." },
  { icon: Banknote, title: "Kobolog Cash offer", desc: "Working capital based on your real cashflow, disbursed via Paystack in minutes." },
  { icon: ShieldCheck, title: "Private & safe", desc: "Your books belong to you. Bank grade encryption from end to end." },
];

const testimonials = [
  { name: "Mama Ngozi", role: "Rice seller, Mile 12", quote: "Before Kobolog AI, I no sabi who owe me. Now I dey collect ₦40k every week wey go don loss." },
  { name: "Tunde A.", role: "POS agent, Surulere", quote: "My loan from Kobolog Cash helped me increase float. I dey make double now, swear." },
  { name: "Chioma O.", role: "Salon owner, Yaba", quote: "I just talk to Kobolog like person. E sabi everything wey I sell that day." },
];

export default function Landing() {
  const cta = useWhatsAppCta();
  const waLive = cta.channel === "whatsapp";
  return (
    <div className="min-h-[100dvh] bg-kudi-cream text-kudi-ink">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-kudi-cream/85 backdrop-blur-md border-b border-border/60">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 rounded-xl bg-kudi-green flex items-center justify-center text-white font-heading font-bold">K</div>
            <span className="font-heading font-bold text-xl">Kobolog AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/signin" className="text-sm font-semibold text-kudi-ink/70 hover:text-kudi-green px-3 py-2 rounded-full hover:bg-white transition" data-testid="nav-signin">Log in</Link>
            <a
              href={WAITLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 bg-kudi-green text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-kudi-green-dark transition shadow-sm"
              data-testid="nav-join"
            >
              Join Waitlist
            </a>
          </div>
        </div>
      </nav>

      {/* Hero: pre-launch, single high contrast waitlist CTA, mobile-first */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 ankara-watermark pointer-events-none" aria-hidden />
        <div className="absolute inset-0 noise-overlay opacity-30 pointer-events-none" aria-hidden />
        <div className="max-w-6xl mx-auto px-5 pt-10 pb-14 md:pt-20 md:pb-24 grid md:grid-cols-2 gap-10 md:gap-12 items-center relative">
          <div className="text-center md:text-left">
            <span className="inline-flex items-center gap-2 bg-kudi-green/10 text-kudi-green text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full" data-testid="hero-prelaunch-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-kudi-green animate-pulse" />
              Waitlist Open
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight">
              Track your{" "}
              <RotatingKeyword
                words={["sales", "expenses", "bookkeeping", "cash flow", "debt", "income", "revenue", "profits"]}
                className="text-kudi-green"
              />{" "}
              <br className="hidden md:block" />
              with AI inside WhatsApp.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-kudi-ink/70 max-w-xl mx-auto md:mx-0 leading-relaxed">
              The easiest way to run your books, right inside WhatsApp. Just chat, we handle the rest.
            </p>
            <div className="mt-7 flex flex-col items-center md:items-start gap-3">
              <a
                href={WAITLIST_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-kudi-green text-white px-8 py-4 rounded-full font-heading font-bold text-base sm:text-lg shadow-xl shadow-kudi-green/30 hover:bg-kudi-green-dark transition active:scale-[0.98]"
                data-testid="hero-waitlist-cta"
              >
                <MessageSquare className="w-5 h-5" />
                Join the Launch Waitlist
              </a>
              <p className="text-sm text-kudi-ink/60 max-w-sm text-center md:text-left" data-testid="hero-waitlist-subtext">
                {WAITLIST_SUBTEXT}
              </p>
            </div>
            <div className="mt-7 flex items-center gap-3 text-xs sm:text-sm text-kudi-ink/60 justify-center md:justify-start">
              <div className="flex -space-x-2">
                {[1,2,3,4].map((idx) => (
                  <div key={`avatar-${idx}`} className="w-8 h-8 rounded-full bg-gradient-to-br from-kudi-green to-kudi-terracotta border-2 border-kudi-cream" />
                ))}
              </div>
              <span className="font-medium text-kudi-ink/70" data-testid="hero-social-proof">Growing daily. Join the community.</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-kudi-green/10 to-kudi-terracotta/10 rounded-[2rem] blur-2xl" aria-hidden />
            <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-2xl">
              <img src={HERO_IMG} alt="Nigerian market trader smiling" className="w-full h-[360px] sm:h-[460px] md:h-[520px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-kudi-green text-white flex items-center justify-center font-heading font-bold">K</div>
                  <div className="flex-1">
                    <div className="text-xs text-kudi-ink/60">Kobolog AI</div>
                    <div className="text-sm font-medium">Mama Ngozi, you don sell <span className="text-kudi-green font-bold tabular-nums">₦87,500</span> today 💪</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* See it in action — animated WhatsApp demo. Lives below the hero so the
          marketing hero stays untouched. Replaced by REACT_APP_DEMO_VIDEO_URL when set. */}
      {!process.env.REACT_APP_DEMO_VIDEO_URL && (
        <section className="bg-white border-t border-border" data-testid="hero-demo-section">
          <div className="max-w-6xl mx-auto px-5 py-14 md:py-20">
            <div className="text-center mb-10 md:mb-12">
              <div className="text-xs text-kudi-green font-semibold uppercase tracking-[0.18em]">See it in action</div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mt-2">
                A 30-second conversation. <br className="hidden sm:block"/>
                <span className="text-kudi-green">Watch your books update.</span>
              </h2>
              <p className="mt-3 text-kudi-ink/65 max-w-xl mx-auto">
                This is exactly how it feels in WhatsApp. The animation replays on its own, no signup required.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
              <div className="relative max-w-[380px] mx-auto w-full" data-testid="hero-demo">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-kudi-green text-white text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-full shadow z-10">
                  Live · auto-demo
                </div>
                <WhatsAppChatDemo />
              </div>
              <ul className="space-y-5">
                {[
                  { t: "0:00", h: "Trader types or speaks", d: "\"I sold 3 bags of rice ₦45,000.\" Voice or text, Pidgin or English. No menus, no buttons." },
                  { t: "0:03", h: "Kobolog logs it instantly", d: "Stock drops by 3 bags. Today's sales jumps to ₦87,500. Cash position updates. All in 3 seconds." },
                  { t: "0:12", h: "Ask anything about your shop", d: "Ask \"Who owes me?\" for the full debtor list. Ask \"How much stock?\" to see what's left. Kobolog knows your business." },
                  { t: "0:25", h: "Borrow capital instantly", d: "When your sales support it, Kobolog offers ₦5k to ₦1M loans, accepted in chat and in your bank within minutes." },
                ].map((row, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-xl bg-kudi-green/10 text-kudi-green font-mono font-bold flex items-center justify-center text-xs">{row.t}</div>
                    <div>
                      <div className="font-heading text-lg font-semibold">{row.h}</div>
                      <p className="text-sm text-kudi-ink/65 leading-relaxed mt-0.5">{row.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center mt-10 md:mt-12 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <WhatsAppCta testid="demo-section-cta" label="Try it now on WhatsApp" />
              <Link
                to="/signin"
                className="px-6 py-3.5 rounded-full font-semibold border-2 border-kudi-ink/15 bg-white hover:border-kudi-green hover:text-kudi-green text-kudi-ink/85 transition-colors flex items-center gap-2"
                data-testid="demo-section-web-cta"
              >
                <span>Use web dashboard</span>
                <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Explainer video — set REACT_APP_DEMO_VIDEO_URL in /app/frontend/.env to enable.
          Supports YouTube embed URLs (https://www.youtube.com/embed/...) or direct mp4. */}
      {process.env.REACT_APP_DEMO_VIDEO_URL && (
        <section className="bg-white border-t border-border" data-testid="demo-video-section">
          <div className="max-w-4xl mx-auto px-5 py-12">
            <div className="text-center mb-6">
              <div className="text-xs text-kudi-green font-semibold uppercase tracking-wider">See it in action</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mt-1">2 minutes &middot; how Kobolog runs your shop</h2>
            </div>
            <div className="aspect-video rounded-2xl overflow-hidden border border-border shadow-md bg-black">
              {process.env.REACT_APP_DEMO_VIDEO_URL.includes("youtube") || process.env.REACT_APP_DEMO_VIDEO_URL.includes("youtu.be") ? (
                <iframe
                  src={process.env.REACT_APP_DEMO_VIDEO_URL}
                  title="Kobolog AI demo"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="demo-video-iframe"
                />
              ) : (
                <video
                  src={process.env.REACT_APP_DEMO_VIDEO_URL}
                  controls
                  className="w-full h-full"
                  data-testid="demo-video-player"
                  preload="metadata"
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* Two ways to use Kobolog — equal first-class paths */}
      <section className="bg-kudi-cream/40 border-y border-border" data-testid="two-paths-section">
        <div className="max-w-6xl mx-auto px-5 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <div className="text-xs text-kudi-green font-semibold uppercase tracking-[0.18em]">After you join · Two ways. One business.</div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mt-2">Use Kobolog your way.</h2>
            <p className="mt-3 text-kudi-ink/65">
            You register once on the web (30 seconds, email plus business info). After that, use whichever channel fits the moment: daily logging on WhatsApp, setup and charts on Web. Same data, both ways.
          </p>
          <p className="mt-4 text-sm font-semibold text-kudi-ink/80 italic">
            WhatsApp for daily logging. Web for setup, charts &amp; admin. One business, two doors.
          </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* WhatsApp card */}
            <div className="bg-white border-2 border-kudi-green/15 rounded-3xl p-7 md:p-8 relative overflow-hidden flex flex-col" data-testid="path-whatsapp">
              <div className="absolute top-5 right-5 bg-kudi-green/10 text-kudi-green text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">Recommended for daily use</div>
              <div className="w-12 h-12 rounded-2xl bg-kudi-green text-white flex items-center justify-center text-2xl">📱</div>
              <h3 className="mt-5 font-heading text-2xl font-bold">On WhatsApp</h3>
              <p className="mt-2 text-kudi-ink/65 text-sm">For traders who already live in WhatsApp. Zero learning curve.</p>
              <ul className="mt-5 space-y-2.5 text-sm flex-1">
                {[
                  "Log sales by text, voice or photo of receipts",
                  `Ask: "Who owes me?", "Stock?", "Today's sales?"`,
                  "Receive Daily Diary, Weekly & Monthly PNG reports",
                  "Type \"menu\" anytime to see all commands",
                  "Works in Pidgin, Yoruba, Hausa, Igbo + English",
                ].map((line, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-kudi-ink/80">
                    <span className="text-kudi-green mt-0.5">✓</span> <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <WhatsAppCta testid="path-wa-cta" label="Open WhatsApp" fallbackLabel="Sign up first" />
                <div className="text-[11px] text-kudi-ink/50 mt-2">Already registered? Tap above to chat.</div>
              </div>
            </div>

            {/* Web card */}
            <div className="bg-white border-2 border-border rounded-3xl p-7 md:p-8 relative overflow-hidden flex flex-col" data-testid="path-web">
              <div className="absolute top-5 right-5 bg-kudi-terracotta/10 text-kudi-terracotta text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">For setup &amp; admin</div>
              <div className="w-12 h-12 rounded-2xl bg-kudi-terracotta text-white flex items-center justify-center text-2xl">🌐</div>
              <h3 className="mt-5 font-heading text-2xl font-bold">On Web</h3>
              <p className="mt-2 text-kudi-ink/65 text-sm">For things WhatsApp cannot do well: forms, charts, admin.</p>
              <ul className="mt-5 space-y-2.5 text-sm flex-1">
                {[
                  "Onboard your business: name, language, bank account",
                  "Accept loans (pick amount, schedule, bank in one screen)",
                  "View interactive charts, debtor tables, search & filter",
                  "Set up your Paystack DVA (virtual bank account)",
                  "Admin console for managing 10s or 100s of traders",
                ].map((line, i) => (
                  <li key={i} className="flex gap-2.5 items-start text-kudi-ink/80">
                    <span className="text-kudi-terracotta mt-0.5">✓</span> <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <Link
                  to="/signin"
                  className="inline-flex items-center gap-2 bg-kudi-terracotta text-white px-6 py-3.5 rounded-full font-semibold hover:bg-kudi-terracotta/90 transition-colors"
                  data-testid="path-web-cta"
                >
                  Open web dashboard <span>→</span>
                </Link>
                <div className="text-[11px] text-kudi-ink/50 mt-2">New here? Tap above to register.</div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-kudi-ink/50 mt-8">
            Everything syncs in real time. Log a sale in WhatsApp → see it on Web instantly. Accept a loan on Web → get the confirmation in WhatsApp.
          </p>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-border bg-white py-4 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap gap-12 text-sm font-medium text-kudi-ink/50">
          {["a", "b"].map((side) => (
            <div key={`marquee-${side}`} className="flex gap-12 items-center shrink-0">
              <span>📍 Mile 12 Market</span>
              <span>📍 Computer Village</span>
              <span>📍 Balogun Market</span>
              <span>📍 Onitsha Main Market</span>
              <span>📍 Aba Ariaria</span>
              <span>📍 Wuse Market</span>
              <span>📍 Mile 1, PH</span>
              <span>📍 Mile 12 Market</span>
              <span>📍 Computer Village</span>
              <span>📍 Balogun Market</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">
            Six things that make running your business feel easy.
          </h2>
          <p className="mt-4 text-kudi-ink/65 text-lg">
            We replaced the notebook, the calculator, and the constant stress with one smart chat.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={f.title} className="bg-white border border-border rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`feature-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-accent flex items-center justify-center text-kudi-green mb-4">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-kudi-ink/65 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-kudi-green text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 ankara-watermark opacity-10" aria-hidden />
        <div className="max-w-6xl mx-auto px-5 relative">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight max-w-2xl">
            From &quot;wahala&quot; to &quot;wawu&quot; in 3 steps.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: waLive ? "Open WhatsApp & say Hi" : "Sign up with your phone", d: waLive ? "Tap our WhatsApp link or message +234... directly. We reply in 5 seconds." : "Just your number. We send a code. You're in. Less than 30 seconds." },
              { n: "02", t: "Tell Kobolog AI your sales", d: "Type or talk. \"Sold rice ₦45k, customer Ibrahim took on credit.\" That's it." },
              { n: "03", t: "Watch your money grow", d: "See your real profit, collect debts, unlock loans. Every. Single. Day." },
            ].map((s) => (
              <div key={s.n} className="bg-white/10 backdrop-blur border border-white/15 rounded-2xl p-6">
                <div className="font-heading text-5xl font-bold text-white/30">{s.n}</div>
                <h3 className="font-heading text-xl font-semibold mt-3">{s.t}</h3>
                <p className="mt-2 text-white/75 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-[2rem] overflow-hidden border border-border">
            <img src={TESTI_IMG} alt="Business owner checking phone" className="w-full h-[460px] object-cover" />
          </div>
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
              Built for the real bosses of Naija.
            </h2>
            <p className="mt-4 text-kudi-ink/65 text-lg">
              From market traders to salon owners, POS agents to food vendors, Kobolog adapts to how you already do business.
            </p>
            <div className="mt-7 space-y-4">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-white border border-border rounded-2xl p-5">
                  <p className="text-kudi-ink leading-relaxed">&quot;{t.quote}&quot;</p>
                  <div className="mt-3 text-sm">
                    <span className="font-semibold">{t.name}</span>
                    <span className="text-kudi-ink/60">, {t.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-5 pb-24">
        <div className="bg-gradient-to-br from-kudi-terracotta to-kudi-green rounded-[2rem] p-8 sm:p-10 md:p-14 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 noise-overlay opacity-50" aria-hidden />
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight relative">
            Be first in line when we launch.
          </h2>
          <p className="mt-4 text-white/85 text-base sm:text-lg max-w-xl mx-auto relative">
            {WAITLIST_SUBTEXT}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 relative">
            <a
              href={WAITLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-kudi-green px-8 py-4 rounded-full font-heading font-bold text-base sm:text-lg hover:bg-kudi-cream shadow-xl transition active:scale-[0.98]"
              data-testid="footer-waitlist-cta"
            >
              <MessageSquare className="w-5 h-5" />
              Join the Launch Waitlist
            </a>
            <span className="text-xs text-white/75">Already a user? <Link to="/signin" className="underline" data-testid="footer-login-link">Log in</Link></span>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-white py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-kudi-ink/60">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-kudi-green flex items-center justify-center text-white text-xs font-bold">K</div>
            <span className="font-heading font-semibold">Kobolog AI</span>
            <span>· Made in Lagos, for the world.</span>
          </div>
          <div className="flex items-center gap-5">
            <a
              href="mailto:support@kobolog.com.ng?subject=Kobolog%20support%20request"
              className="hover:text-kudi-green transition-colors"
              data-testid="footer-contact-support"
            >
              Contact Support
            </a>
            <a
              href="https://wa.me/2347010599993?text=Hi%20Kobolog%20team%2C%20I%20need%20help%20with"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-kudi-green transition-colors"
              data-testid="footer-support-whatsapp"
            >
              Support on WhatsApp
            </a>
            <div>© {new Date().getFullYear()} Kobolog AI</div>
          </div>
        </div>
      </footer>

      {/* Floating support chat widget */}
      <SupportWidget />
    </div>
  );
}

/**
 * RotatingKeyword — cycles through the given words with a subtle fade/slide.
 * Used in the hero headline per Iter32 landing rewrite.
 */
function RotatingKeyword({ words, className = "", interval = 2200 }) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("in"); // 'in' | 'out'
  useEffect(() => {
    if (!words?.length) return;
    let mounted = true;
    const step = () => {
      if (!mounted) return;
      setPhase("out");
      setTimeout(() => {
        if (!mounted) return;
        setIdx((i) => (i + 1) % words.length);
        setPhase("in");
      }, 260);
    };
    const t = setInterval(step, interval);
    return () => { mounted = false; clearInterval(t); };
  }, [words, interval]);
  const active = words?.[idx] ?? "";
  // Reserve horizontal space equal to the LONGEST word so the layout doesn't jitter.
  const longest = (words || []).reduce((a, b) => (a.length >= b.length ? a : b), "");
  return (
    <span className={`relative inline-block align-baseline ${className}`} data-testid="hero-rotating-word">
      {/* invisible sizer keeps width stable while the visible word swaps */}
      <span aria-hidden className="invisible whitespace-nowrap">{longest}</span>
      <span
        className={`absolute left-0 top-0 whitespace-nowrap transition-all duration-300 ease-out ${
          phase === "in" ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
        }`}
      >
        {active}
      </span>
    </span>
  );
}
